import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';

import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Feature } from '@app/api';
import { LockFeatureChip } from '@app/core';
import { useFormikError, useOptionalField } from './FormikFields';
import { ErrorMsg } from './ErrorMsg';
import { useFormikContext } from 'formik';
import { RawCheckbox } from './RawCheckbox';
import { useTranslation } from 'react-i18next';
import { Input } from '@mui/material';
import { get } from 'lodash';

export interface CheckboxBlockFieldProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  containerClass?: string;
  value?: any;
  name?: string;
  error?: string;
  noMargin?: boolean;
  checked?: boolean;
  indeterminate?: boolean;
  readOnly?: boolean;
  editable?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => boolean | void;
  children?: React.ReactNode;
  variant?: 'green' | 'greenSelectedNoIconColor';
  noErrorMsg?: boolean;
  icon?: string;
  iconClassName?: string;
  labelClassName?: string;
  onEditValueChange?: (value: string) => void;
  feature?: Feature;
  featureChildren?: React.ReactNode;
  trackingName?: string;
  otherPlaceholder?: string;
}

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  container: {
    width: '100%',
    display: 'flex',
    gap: 8,
    alignItems: 'stretch',
  },
  containerSingle: {
    display: 'block',
  },
  noMargin: {
    marginBottom: 0,
    marginTop: 0,
  },
  box: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: theme.borderRadius.std,
    minHeight: theme.height.regular,
    width: '100%',
    padding: [[theme.marginBase, theme.marginBase * 2]],
    border: {
      width: 1,
      style: 'solid',
      color: theme.colors.grayBorder,
    },
    flexShrink: 0,

    '&:hover > .checkbox-icon': {
      color: theme.colors.blueAccessible,
    },
  },
  boxSelected: {
    backgroundColor: theme.colors.grayBackground,
  },
  boxEditableSelected: {
    width: 'auto',
  },
  boxGreen: {
    '&:hover > .checkbox-icon': {
      color: theme.colors.greenAccessible,
    },
  },
  boxGreenSelected: {
    backgroundColor: theme.colors.greenMuted,
    borderColor: theme.colors.green,
    '& > .checkbox-icon, &:hover > .checkbox-icon, & > .end-icon': {
      color: theme.colors.green,
    },
  },
  boxGreenSelectedNoIconColor: {
    backgroundColor: theme.colors.greenMuted,
    borderColor: theme.colors.green,
    '& > .checkbox-icon, &:hover > .checkbox-icon': {
      color: theme.colors.green,
    },
  },
  boxDisabled: {
    cursor: 'default',
    '& > .checkbox-icon, &:hover > .checkbox-icon': {
      color: theme.colors.grayBorder,
    },
    opacity: 0.5,
  },
  label: {
    ...theme.fonts.caption,
    flex: 1,
    marginLeft: theme.marginBase,
    color: theme.colors.inkTertiary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  labelSelected: {
    color: theme.colors.inkPrimary,
  },
  boxInvalid: {
    '& > .checkbox-icon, &:hover > .checkbox-icon': {
      color: theme.colors.red,
    },
  },
  editableInputWrapper: {
    overflow: 'hidden',
    transition:
      'transform 0.45s cubic-bezier(0.25, 0.8, 0.25, 1), opacity 0.45s ease',
    transform: 'translateX(10%)',
    flexGrow: 1,
    opacity: 0,
  },
  editableInputCollapsed: {
    transform: 'translateX(10%)',
    maxWidth: 0,
    opacity: 0,
  },
  editableInputExpanded: {
    transform: 'translateX(0)',
    maxWidth: '100%',
    opacity: 1,
  },
  editableInputBox: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: theme.borderRadius.std,
    minHeight: theme.height.regular,
    padding: [[theme.marginBase, theme.marginBase * 2]],
    border: {
      width: 1,
      style: 'solid',
      color: theme.colors.grayBorder,
    },
    backgroundColor: theme.colors.white,
  },
  editableInput: {
    '& .MuiInputBase-input': {
      ...theme.fonts.caption,
    },
  },
  icon: {
    fontSize: theme.iconSize.large,
    color: theme.colors.inkTertiary,
  },
}));

export const CheckboxBlockField = ({
  label,
  className,
  value,
  name,
  onChange,
  error,
  checked,
  disabled,
  editable,
  children,
  variant,
  noErrorMsg,
  icon,
  iconClassName,
  labelClassName,
  onEditValueChange,
  feature,
  featureChildren,
  trackingName,
  otherPlaceholder,
  ...rest
}: CheckboxBlockFieldProps) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t, i18n } = useTranslation('common');

  const [editedValue, setEditedValue] = useState(
    value !== undefined ? value : '',
  );

  const errorMsg = useFormikError({ name, error });
  const formik = useFormikContext<any>();

  const shouldDisplayError = useMemo(() => {
    if (!name || !formik) {
      return false;
    }
    const error: any = get(formik.errors, name);
    if (!error) {
      return false;
    }
    return !(
      error?.value &&
      Array.isArray(error.value) &&
      error.value.includes(value)
    );
  }, [formik, name, value]);

  const _onChange = useCallback(
    (e: any) => {
      if (disabled) {
        return;
      }
      if (onChange) {
        const res = onChange(e);
        if (!res) {
          return;
        }
      }
      if (!name || !formik) {
        return;
      }

      formik.setFieldValue(
        name,
        e.target.checked
          ? rest.type === 'radio'
            ? value
            : editedValue === undefined
            ? true
            : editedValue
          : undefined,
      );
    },
    [formik, name, disabled, onChange, editedValue, rest.type, value],
  );

  const { field } = useOptionalField({ name });

  const isChecked =
    checked !== undefined
      ? checked
      : rest.type === 'radio'
      ? !!name && field.value === value
      : !!name && field.value === editedValue;

  const displayedLabel = useMemo(() => {
    if (children) {
      return children;
    }
    if (label) {
      return i18n.exists(label) ? t(label) : label;
    }
    return undefined;
  }, [label, children, i18n, t]);

  const handleInputChange = useCallback(
    (e: any) => {
      const newValue = e.target.value;
      setEditedValue(newValue);
      if (!!name && formik) {
        formik.setFieldValue(name, newValue);
      }
      onEditValueChange && onEditValueChange(newValue);
    },
    [name, formik, onEditValueChange],
  );

  return (
    <div
      className={classnames(
        classes.container,
        {
          [classes.containerSingle]: !editable,
        },
        className,
      )}
    >
      <label
        className={classnames(
          classes.box,
          {
            [classes.boxInvalid]: !!errorMsg && shouldDisplayError,
            [classes.boxSelected]: isChecked,
            [classes.boxEditableSelected]: editable && isChecked,
            [classes.boxDisabled]: disabled,
            [classes.boxGreenSelected]: variant === 'green' && isChecked,
            [classes.boxGreenSelectedNoIconColor]:
              variant === 'greenSelectedNoIconColor' && isChecked,
            [classes.boxGreen]: variant === 'green',
          },
          labelClassName,
        )}
      >
        <RawCheckbox
          noLabel
          type="checkbox"
          className="checkbox-icon"
          name={name}
          {...rest}
          onChange={_onChange}
          value={editedValue}
          checked={isChecked}
          disabled={disabled}
          data-tracking-name={trackingName}
        />
        {displayedLabel && (
          <div
            className={classnames(classes.label, {
              [classes.labelSelected]: isChecked,
            })}
          >
            {feature && (
              <LockFeatureChip feature={feature} children={featureChildren} />
            )}
            {displayedLabel}
          </div>
        )}
        {!!icon && (
          <i
            className={classnames(
              classes.icon,
              icon,
              'end-icon',
              iconClassName,
            )}
          />
        )}
      </label>
      {editable && (
        <div
          className={classnames(classes.editableInputWrapper, {
            [classes.editableInputCollapsed]: !isChecked,
            [classes.editableInputExpanded]: isChecked,
          })}
        >
          {isChecked && (
            <div className={classes.editableInputBox}>
              <Input
                name={name}
                onChange={handleInputChange}
                className={classes.editableInput}
                value={editedValue}
                disableUnderline
                autoFocus
                fullWidth
                placeholder={otherPlaceholder}
              />
            </div>
          )}
        </div>
      )}
      {!noErrorMsg && shouldDisplayError && <ErrorMsg error={errorMsg} />}
    </div>
  );
};
